import useNotificationApi from "app/hooks/useNotificationApi";
import Swal from "sweetalert2";
import {
  CAPSULE_STRATEGY_OPTIONS,
  NotificationPreferences,
  PAYMENT_STRATEGY_OPTIONS,
} from "app/types/NotificationPreferences";
import { useState, useEffect, useCallback } from "react";
import { UseNotificationControlProps } from ".";
import { useHistory } from "react-router-dom";
import store from "app/store/store";

const useNotificationControlModel = ({
  idUser,
}: UseNotificationControlProps = {}) => {
  // Estado das preferências
  const [
    preferences,
    setPreferences,
  ] = useState<NotificationPreferences | null>(null);
  const [
    originalPreferences,
    setOriginalPreferences,
  ] = useState<NotificationPreferences | null>(null);

  // Estado de UI
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const history = useHistory();

  const { auth } = store.getState();
  const isAdmin =
    auth && auth.user && auth.user.profile.nome.toUpperCase() === "ADMIN";

  const capsuleStrategies = CAPSULE_STRATEGY_OPTIONS;
  const paymentStrategies = PAYMENT_STRATEGY_OPTIONS;

  const {
    getPreferences,
    updatePreferences,
    getUserDetails,
  } = useNotificationApi();

  // Calcular se há mudanças
  const hasChanges =
    JSON.stringify(preferences) !== JSON.stringify(originalPreferences);

  // Carregar preferências ao montar o componente
  useEffect(() => {
    getData();
  }, [idUser]);

  const validUserConfig = async () => {
    if (!idUser) {
      await userNotAllowed(
        "Usuário inválido para configurar preferências de notificação.",
      );
      return;
    }
    if (!auth || !auth.user) {
      await userNotAllowed("Usuário não autenticado.");
      return;
    }
    if (auth.user.id !== idUser && !isAdmin) {
      await userNotAllowed(
        "Você não pode configurar as preferências de notificação de outro usuário.",
      );
      return;
    }
  };

  const userNotAllowed = async (message: string) => {
    await Swal.fire({ icon: "warning", title: "Atenção", text: message });
    history.push("/admin/users");
  };

  const getData = async () => {
    await validUserConfig();

    if (idUser) {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getPreferences(idUser);
        setPreferences(data);
        setOriginalPreferences(data);

        const resUserDetails = await getUserDetails(idUser);
        if (resUserDetails) {
          setUserDetails(resUserDetails);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Erro ao carregar preferências de notificação",
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Atualizar preferência específica
  const updatePreference = useCallback(
    <PreferenceKey extends keyof NotificationPreferences>(
      key: PreferenceKey,
      value: NotificationPreferences[PreferenceKey],
    ) => {
      setPreferences(prev => {
        if (!prev) return null;
        return { ...prev, [key]: value };
      });
      setError(null);
    },
    [],
  );

  // Salvar preferências
  const handleSave = useCallback(async () => {
    if (!preferences || !idUser) {
      setError("Dados incompletos para salvar");
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      const updated = await updatePreferences(idUser, preferences);
      setPreferences(updated);
      setOriginalPreferences(updated);
      setSuccessMessage(updated.message || "Preferências salvas com sucesso!");

      // Limpar mensagem de sucesso após 4 segundos
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Erro ao salvar preferências de notificação",
      );
    } finally {
      setIsSaving(false);
    }
  }, [preferences, idUser]);

  // Descartar mudanças
  const handleDiscard = useCallback(() => {
    setPreferences(originalPreferences);
    setError(null);
  }, [originalPreferences]);

  return {
    preferences,
    originalPreferences,
    capsuleStrategies,
    paymentStrategies,
    isAdmin,
    userDetails,
    isLoading,
    isSaving,
    error,
    successMessage,
    hasChanges,
    updatePreference,
    handleSave,
    handleDiscard,
  };
};

export default useNotificationControlModel;
